import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { Account } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from '@/pages/ProjectsPage.module.css';

type AudienceAccountRow = {
  account_id: string;
  source: string;
  added_at: string;
  name: string;
  website: string | null;
  domain_normalized: string | null;
  industry: string | null;
  company_size: string | null;
  contact_count_in_audience: number;
  contact_count_total: number;
};

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Handmatig',
  rule: 'Filter',
  scraper: 'Scraper',
  import: 'Import',
};

export function AudienceAccountsPanel({
  audienceId,
  projectId,
}: {
  audienceId: string;
  projectId: string;
}) {
  const qc = useQueryClient();
  const [accountQ, setAccountQ] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const membersQ = useQuery({
    queryKey: ['audience-accounts', audienceId],
    queryFn: () =>
      apiFetch<{ accounts: AudienceAccountRow[] }>(
        `/api/audiences/${audienceId}/accounts?limit=200`,
      ),
  });

  const pickQ = useQuery({
    queryKey: ['accounts-pick', projectId, accountQ],
    enabled: Boolean(projectId),
    queryFn: () => {
      const params = new URLSearchParams({ project_id: projectId });
      if (accountQ.trim()) params.set('q', accountQ.trim());
      return apiFetch<{ accounts: Account[] }>(`/api/accounts?${params}`);
    },
  });

  const inAudience = new Set((membersQ.data?.accounts ?? []).map((a) => a.account_id));

  const addAccounts = useMutation({
    mutationFn: (accountIds: string[]) =>
      apiFetch(`/api/audiences/${audienceId}/accounts`, {
        method: 'POST',
        body: JSON.stringify({ account_ids: accountIds, source: 'manual' }),
      }),
    onSuccess: () => {
      setPicked(new Set());
      void qc.invalidateQueries({ queryKey: ['audience-accounts', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience-members', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });

  const removeAccount = useMutation({
    mutationFn: (accountId: string) =>
      apiFetch(`/api/audiences/${audienceId}/accounts/${accountId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['audience-accounts', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience-members', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
    },
  });

  return (
    <section className={styles.card}>
      <h2>Bedrijven in doelgroep</h2>
      <p className={styles.mutedSmall}>
        Bedrijven bepalen de omvang (# accounts). Contacten per bedrijf worden meegenomen in
        campagnes via de gesynchroniseerde lijst.
      </p>

      {membersQ.isLoading ? <LoadingState /> : null}
      {!membersQ.isLoading && !(membersQ.data?.accounts ?? []).length ? (
        <StateMessage title="Nog geen bedrijven">
          Voeg bedrijven toe of koppel contacten met een account in CRM.
        </StateMessage>
      ) : null}

      <ul className={styles.list}>
        {(membersQ.data?.accounts ?? []).map((a) => (
          <li key={a.account_id} className={styles.row}>
            <span>
              <strong>{a.name}</strong>
              {a.domain_normalized ? (
                <span className={styles.mutedSmall}> · {a.domain_normalized}</span>
              ) : null}
              <br />
              <span className={styles.mutedSmall}>
                {a.contact_count_in_audience} / {a.contact_count_total} contacten in doelgroep ·{' '}
                {SOURCE_LABELS[a.source] ?? a.source}
              </span>
            </span>
            <Button
              type="button"
              variant="ghost"
              onClick={() => removeAccount.mutate(a.account_id)}
              disabled={removeAccount.isPending}
            >
              Verwijderen
            </Button>
          </li>
        ))}
      </ul>

      <h3 style={{ marginTop: 'var(--space-lg)' }}>Toevoegen uit accounts</h3>
      <p className={styles.mutedSmall}>
        Alle contacten van het gekozen bedrijf worden aan deze doelgroep toegevoegd.{' '}
        <Link to="/app/accounts">Accounts beheren</Link>
      </p>
      <Field label="Zoeken">
        <TextInput value={accountQ} onChange={(e) => setAccountQ(e.target.value)} />
      </Field>
      {pickQ.isLoading ? <LoadingState /> : null}
      <ul className={styles.list}>
        {(pickQ.data?.accounts ?? [])
          .filter((a) => !inAudience.has(a.id))
          .slice(0, 30)
          .map((a) => (
            <li key={a.id}>
              <label className={styles.row}>
                <span>
                  <input
                    type="checkbox"
                    checked={picked.has(a.id)}
                    onChange={(e) => {
                      setPicked((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(a.id);
                        else next.delete(a.id);
                        return next;
                      });
                    }}
                  />{' '}
                  {a.name}
                  <span className={styles.mutedSmall}>
                    {' '}
                    · {a.contact_count ?? 0} contacten
                    {a.domain_normalized ? ` · ${a.domain_normalized}` : ''}
                  </span>
                </span>
              </label>
            </li>
          ))}
      </ul>
      <Button
        type="button"
        disabled={picked.size === 0 || addAccounts.isPending}
        onClick={() => addAccounts.mutate([...picked])}
      >
        {picked.size} bedrijf/bedrijven toevoegen
      </Button>
    </section>
  );
}
